import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import { TbEye, TbPlus } from "react-icons/tb";
import { CurrencyIcon } from "../../../../../Components/Atoms/CurrencyIcon";
import { GridPagination } from "../../../../../Components/Molecules/GridHistory/GridPagination";
import {
  buildBaasStableCdnIconUrl,
  resolveBaasStableIconUrl,
} from "../../../../../Services/baasStablesWalletApi";
import { paginateList } from "../../../Extract/extractFilters";
import { formatStable } from "../../../Stables/constants";
import { buildBaasStablesWalletDetailPath } from "../../constants";
import { BaasStablesNetworkBadge } from "./BaasStablesNetworkBadge";
import { BaasStablesWalletStatusBadge } from "./BaasStablesWalletStatusBadge";

const truncateAddress = (address) => {
  if (!address || address.length < 12) return address ?? "—";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const BaasStablesWalletsTable = ({
  wallets = [],
  onCreate,
  showInstanceColumn = false,
}) => {
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedItems = useMemo(
    () => paginateList(wallets, page, rowsPerPage),
    [wallets, page, rowsPerPage],
  );

  useEffect(() => {
    setPage(1);
  }, [wallets.length]);

  const handleSelect = (wallet) => {
    history.push(buildBaasStablesWalletDetailPath(wallet.id));
  };

  return (
    <section className="min-w-0 w-full space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="dashboard-section-label">{intl.get("baas.stablesWallet.listTitle")}</p>
        {typeof onCreate === "function" ? (
          <button
            type="button"
            className="btn-primary !h-8 !min-h-8 shrink-0 !px-3 !text-xs"
            onClick={onCreate}
          >
            <TbPlus className="h-3.5 w-3.5" aria-hidden />
            {intl.get("baas.stablesWallet.actions.create")}
          </button>
        ) : null}
      </div>

      <div className="app-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.wallet")}</th>
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.asset")}</th>
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.network")}</th>
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.status")}</th>
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.entity")}</th>
                {showInstanceColumn ? (
                  <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.instance")}</th>
                ) : null}
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.linkedAccount")}</th>
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.availableBalance")}</th>
                <th className="px-4 py-3">{intl.get("baas.stablesWallet.columns.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {paginatedItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={showInstanceColumn ? 9 : 8}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {intl.get("baas.stablesWallet.listEmpty")}
                  </td>
                </tr>
              ) : (
                paginatedItems.map((wallet) => (
                  <tr
                    key={wallet.id}
                    className="cursor-pointer hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
                    onClick={() => handleSelect(wallet)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-brand-dark dark:text-gray-100">
                        {wallet.alias}
                      </div>
                      <div className="mt-0.5 font-mono text-xs text-gray-500 dark:text-gray-400">
                        {truncateAddress(wallet.address)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2 font-medium text-gray-600 dark:text-gray-300">
                        <CurrencyIcon
                          asset={wallet.asset}
                          iconUrl={
                            resolveBaasStableIconUrl(wallet.iconUrl, wallet.asset) ||
                            buildBaasStableCdnIconUrl(wallet.asset)
                          }
                          size={18}
                        />
                        {wallet.asset}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <BaasStablesNetworkBadge network={wallet.network} />
                    </td>
                    <td className="px-4 py-3">
                      <BaasStablesWalletStatusBadge status={wallet.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {wallet.entityName}
                    </td>
                    {showInstanceColumn ? (
                      <td className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300">
                        {String(wallet.instanceName || "").trim() || "—"}
                      </td>
                    ) : null}
                    <td className="px-4 py-3">
                      <div className="font-medium text-brand-dark dark:text-gray-100">
                        {wallet.accountAlias}
                      </div>
                      <div className="mt-0.5 font-mono text-xs text-gray-500 dark:text-gray-400">
                        {wallet.accountNumber}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-brand-dark dark:text-gray-100">
                      {`${formatStable(wallet.balances?.available ?? 0)} ${wallet.asset}`}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-[#e7a73f] transition hover:bg-[#e7a73f]/10"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelect(wallet);
                        }}
                      >
                        <TbEye size={14} aria-hidden />
                        {intl.get("baas.stablesWallet.detailAction")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <GridPagination
          total={wallets.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.stablesWallet.paginationLabel")}
        />
      </div>
    </section>
  );
};
